
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Benefits() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const benefits = [
    {
      icon: 'ri-scales-3-line',
      title: '法律思維的嚴謹性',
      description: '在律師事務所培養的邏輯分析習慣，讓我能以結構化方式拆解問題、評估風險，並確保資料解讀的準確性。'
    },
    {
      icon: 'ri-links-line',
      title: '跨領域的獨特優勢',
      description: '法律實務出身，具備訴訟文書、合約審閱與跨機關協調經驗，加上防制洗錢合規研習認證，能在法律科技與金融科技產業中提供別具視角的分析洞察。'
    },
    {
      icon: 'ri-rocket-line',
      title: '自驅學習的執行力',
      description: '在職期間同步完成 530 小時數據分析實務課程，自主開發多個個人專案，展現持續精進 Python、SQL 與資料視覺化技術的主動學習力。'
    },
    {
      icon: 'ri-team-line',
      title: '溝通協作的實務經驗',
      description: '曾協助律師處理 100+ 法律案件，習慣與多方利害關係人協調，能清楚表達複雜資訊並適應快節奏的工作環境。'
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center mb-10 md:mb-24" ref={sectionRef}>
          {/* Left Content */}
          <div>
            <div
              className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <span className="text-sm font-semibold text-orange-700">為什麼選擇我</span>
            </div>
            <h2
              className={`text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
            >
              法律思維×數據能力<br />的複合優勢
            </h2>
            <p
              className={`text-base text-gray-600 leading-relaxed mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '200ms' }}
            >
              我不只是一位正在學習數據技能的人，更是一位帶著法律領域嚴謹思維、實務合規經驗與跨域視角的轉職者。
            </p>

            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300">
                    <i className={`${benefit.icon} text-white text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Stats cards */}
          <div
            className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: '2.5+', label: '年法律工作經驗', icon: 'ri-briefcase-4-line', color: 'from-orange-400 to-rose-500' },
                { value: '100+', label: '協助法律案件', icon: 'ri-file-list-3-line', color: 'from-rose-400 to-rose-600' },
                { value: '795', label: 'TOEIC 多益分數', icon: 'ri-translate-2', color: 'from-amber-400 to-orange-500' },
                { value: '3+', label: '個人數據專案', icon: 'ri-code-box-line', color: 'from-orange-500 to-rose-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-gradient-to-br from-orange-50 to-rose-50 rounded-2xl p-6 border border-orange-100 hover:shadow-lg transition-shadow duration-300">
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <i className={`${stat.icon} text-white text-xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-orange-400 to-rose-400 rounded-full opacity-20 blur-2xl animate-pulse-soft"></div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full opacity-20 blur-2xl animate-pulse-soft" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>

        {/* CTA Section */}
        <div
          ref={ctaRef}
          className={`relative rounded-3xl overflow-hidden transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-rose-500"></div>
          <div className="relative px-16 py-20 text-center">
            <h2
              className={`text-2xl md:text-4xl font-bold text-white mb-6 leading-tight transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '200ms' }}
            >
              對我的背景感興趣？
            </h2>
            <p
              className={`text-lg text-white/90 mb-10 max-w-2xl mx-auto transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '300ms' }}
            >
              歡迎查看我的完整履歷或個人專案，也可以直接與我聯絡——我正積極尋找數據分析師／數據工程師職位。
            </p>
            <div
              className={`flex items-center justify-center gap-4 flex-wrap transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '400ms' }}
            >
              <button
                onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-12 py-4 rounded-full font-bold text-base bg-white text-orange-600 hover:bg-gray-50 hover:scale-105 transition-all duration-300 shadow-xl cursor-pointer whitespace-nowrap"
              >
                聯絡我
              </button>
              <Link
                to="/side-projects"
                className="px-12 py-4 rounded-full font-bold text-base border-2 border-white text-white hover:bg-white hover:text-orange-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                查看個人專案
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
